// src/pages/dashboards/practitioner/PractitionerClients.jsx
// Client roster with tax pack release status. Defaults to the year of assessment
// currently in progress, since that is where new scans land. Switch the selector
// to a completed year to work on a return being filed.

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../../api/practitioners";
import { Card, Select, Input, Loading, EmptyState, Badge, Button } from "../../../components/ui";
import { Download, Search } from "../../../components/icons";
import { money } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

// year of assessment currently in progress
const currentTaxYear = () => {
  const d = new Date();
  return d.getMonth() >= 2 ? d.getFullYear() + 1 : d.getFullYear();
};
// the year practitioners are actually filing: the last completed one
const IN_PROGRESS = currentTaxYear();
const FILING_YEAR = IN_PROGRESS - 1;
const TAX_YEARS = [IN_PROGRESS, FILING_YEAR, FILING_YEAR - 1];

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export default function PractitionerClients() {
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
    const [taxYear, setTaxYear] = useState(IN_PROGRESS);
  const [q, setQ] = useState("");
  const [nudgingId, setNudgingId] = useState(null);
  const [nudged, setNudged] = useState({}); // user_id -> true, this session

  useEffect(() => {
    setData(null);
    setNudged({});
    api.getDashboard({ tax_year: taxYear })
      .then(setData)
      .catch((e) => { toast.error(e.message); setData({ clients: [], released: 0, outstanding: 0 }); });
    // eslint-disable-next-line
  }, [taxYear]);

  const downloadPack = async (client) => {
    try {
      const blob = await api.downloadClientTaxPack(client.user_id, taxYear);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TaxPack-${client.name.replace(/\s+/g, "_")}-TY${taxYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tax pack downloaded");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const nudge = async (client) => {
    setNudgingId(client.user_id);
    try {
            const r = await api.nudgeClient(client.user_id, { tax_year: taxYear });
      setNudged((m) => ({ ...m, [client.user_id]: true }));
      const via = [r.push_sent ? "app" : null, r.emailed ? "email" : null].filter(Boolean);
      toast.success(via.length
        ? `Reminder sent to ${client.name} by ${via.join(" and ")}`
        : `Could not reach ${client.name}`);
    } catch (e) {
      if (e.code === "NUDGE_TOO_SOON") {
        setNudged((m) => ({ ...m, [client.user_id]: true }));
        toast.error("You already reminded them today.");
      } else {
        toast.error(e.message);
      }
    } finally {
      setNudgingId(null);
    }
  };

  const clients = data?.clients || [];
  const totalDeductions = clients.reduce((s, c) => s + (c.deductions_total || 0), 0);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term));
  }, [clients, q]);

  const outstanding = clients.filter((c) => !c.released);

  // nudge everyone still outstanding, sequentially so the rate limit holds
  const nudgeAll = async () => {
    const targets = outstanding.filter((c) => !nudged[c.user_id]);
    if (!targets.length) return;
    if (!window.confirm(`Send a reminder to ${targets.length} outstanding client${targets.length !== 1 ? "s" : ""}?`)) return;
    let sent = 0;
    for (const c of targets) {
      setNudgingId(c.user_id);
      try {
        await api.nudgeClient(c.user_id, { tax_year: taxYear });
        sent += 1;
        setNudged((m) => ({ ...m, [c.user_id]: true }));
      } catch (e) {
        if (e.code === "NUDGE_TOO_SOON") setNudged((m) => ({ ...m, [c.user_id]: true }));
      }
    }
    setNudgingId(null);
    toast.success(`${sent} reminder${sent !== 1 ? "s" : ""} sent`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="text-2xl font-display font-bold">Clients</h1>
        <div className="flex items-center gap-2">
          {outstanding.length > 1 && (
            <Button variant="ghost" onClick={nudgeAll} disabled={!!nudgingId}>
              Remind all outstanding
            </Button>
          )}
          <Select value={taxYear} onChange={(e) => setTaxYear(parseInt(e.target.value, 10))} className="w-auto">
            {TAX_YEARS.map((y) => (
              <option key={y} value={y}>TY {y}{y === IN_PROGRESS ? " (in progress)" : ""}</option>
            ))}
          </Select>
        </div>
      </div>

      <p className="text-sm text-tb-muted mb-6">
        {taxYear === IN_PROGRESS
          ? "This year of assessment is still running."
          : `Year of assessment ending 28 February ${taxYear}.`}
      </p>

      {/* summary strip */}
      {data && clients.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Outstanding</p>
            <p className="text-xl font-bold mt-1 tabular-nums text-amber-500">{data.outstanding}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Released</p>
            <p className="text-xl font-bold mt-1 tabular-nums text-tb-brand">{data.released}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Clients</p>
            <p className="text-xl font-bold mt-1 tabular-nums">{clients.length}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Combined deductions</p>
            <p className="text-xl font-bold mt-1 tabular-nums">{money(totalDeductions)}</p>
          </Card>
        </div>
      )}

      {/* search */}
      {clients.length > 5 && (
        <div className="relative mb-4 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients"
            className="pl-9"
          />
        </div>
      )}

      {/* client list */}
      {!data ? (
        <Loading label="Loading clients" />
      ) : clients.length === 0 ? (
        <Card className="p-2">
          <EmptyState
            title="No clients linked yet"
            hint="Create a connect code and share it with your clients so they can grant you read-only access."
          />
          <div className="pb-6 flex justify-center">
            <Button onClick={() => navigate("/practitioner/codes")}>Create a connect code</Button>
          </div>
        </Card>
      ) : shown.length === 0 ? (
        <Card className="p-2">
          <EmptyState title="No matches" hint="No client matches that search." />
        </Card>
      ) : (
        <Card className="divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
          {shown.map((c) => (
            <div
              key={c.user_id}
              className="px-4 sm:px-5 py-4 flex items-center gap-4 hover:bg-tb-bg-2/50 dark:hover:bg-tb-d-inset/50 transition-colors"
            >
              {/* clicking the identity area opens the client */}
              <button
                onClick={() => navigate(`/practitioner/client/${c.user_id}`)}
                className="flex items-center gap-4 min-w-0 flex-1 text-left"
              >
                <div className="w-10 h-10 rounded-full shrink-0 grid place-items-center text-sm font-semibold text-tb-brand bg-tb-brand/10">
                  {initials(c.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{c.name}</p>
                  <p className="text-sm text-tb-muted truncate">{c.email}</p>
                </div>
                <div className="hidden sm:block text-right shrink-0">
                  <p className="font-semibold tabular-nums">{money(c.deductions_total)}</p>
                  <p className="text-xs text-tb-muted">{c.expense_count} item{c.expense_count !== 1 ? "s" : ""}</p>
                </div>
              </button>

              {/* status + action */}
              <div className="flex items-center gap-2 shrink-0">
                {c.released ? (
                  <>
                    <Badge tone="green">Released</Badge>
                    <button
                      onClick={() => downloadPack(c)}
                      className="text-tb-brand hover:text-tb-cta transition-colors p-1"
                      title="Download tax pack"
                    >
                      <Download size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <Badge tone="amber">Outstanding</Badge>
                    <Button
                      variant="ghost"
                      onClick={() => nudge(c)}
                      disabled={nudgingId === c.user_id || nudged[c.user_id]}
                    >
                      {nudgingId === c.user_id ? "Sending..." : nudged[c.user_id] ? "Reminded" : "Remind"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}